import { useEffect, useRef, useState } from 'react';
import { ChatMessageStatus, type ChatMessage } from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';

import { StarIcon } from './StarIcon';

interface ParticipantNames {
  myName: string;
  otherName: string;
  otherAvatar?: string;
  otherInitial?: string;
}

interface MessageThreadProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  isTyping?: boolean;
  conversationId?: string;
  participants?: ParticipantNames;
  /** The conversation's customer userId — used to detect the user's own messages. */
  customerUserId?: string;
  /**
   * When provided (super-admin only), a small icon on the last *unanswered* user
   * message re-activates the professional's unread notification for this chat.
   */
  onMarkUnread?: () => void;
  /**
   * Messaggi preferiti: se passato (solo cliente/consulente), accanto a ogni
   * messaggio compare la stella per aggiungerlo/toglierlo dai preferiti.
   */
  onToggleStar?: (messageId: string, starred: boolean) => void;
}

function StarToggle({ starred, onToggle }: { starred: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={starred}
      aria-label={starred ? 'Togli dai preferiti' : 'Aggiungi ai preferiti'}
      title={starred ? 'Togli dai preferiti' : 'Aggiungi ai preferiti'}
      className={`mx-1 mb-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-brand-teal ${
        starred
          ? 'text-brand-orange opacity-100'
          : 'text-neutral-300 hover:bg-brand-orange/10 hover:text-brand-orange sm:opacity-0 sm:group-hover:opacity-100'
      }`}
    >
      <StarIcon filled={starred} />
    </button>
  );
}

function formatMessageTime(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function formatFullTimestamp(dateStr: string | Date): string {
  const date = new Date(dateStr);
  return date.toLocaleString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDateSeparator(dateStr: string | Date): string {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Oggi';
  if (date.toDateString() === yesterday.toDateString()) return 'Ieri';
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** WhatsApp-style status: single check (sent), double check (delivered), blue double check (read) */
function StatusIcon({ status }: { status: ChatMessageStatus }) {
  if (status === ChatMessageStatus.READ) {
    // Blue double check
    return (
      <svg className="h-[14px] w-[14px] text-brand-teal" viewBox="0 0 16 16" fill="none">
        <path d="M1.5 8.5L4.5 11.5L11 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 8.5L8.5 11.5L15 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (status === ChatMessageStatus.DELIVERED) {
    // Grey double check
    return (
      <svg className="h-[14px] w-[14px] text-neutral-400" viewBox="0 0 16 16" fill="none">
        <path d="M1.5 8.5L4.5 11.5L11 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 8.5L8.5 11.5L15 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // Single check (sent)
  return (
    <svg className="h-[14px] w-[14px] text-neutral-300" viewBox="0 0 16 16" fill="none">
      <path d="M3 8.5L6 11.5L13 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HoverTimestamp({ timestamp, isMine, children }: { timestamp: string | Date; isMine: boolean; children: React.ReactNode }) {
  const [show, setShow] = useState(false);

  return (
    <div
      className="relative max-w-[min(80%,440px)] sm:max-w-[min(75%,520px)]"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div
          className={`absolute bottom-full z-10 mb-1.5 whitespace-nowrap rounded-md bg-neutral-900/95 px-2.5 py-1 text-[10px] text-white/70 shadow-lg backdrop-blur-sm ${
            isMine ? 'right-0' : 'left-0'
          }`}
        >
          {formatFullTimestamp(timestamp)}
        </div>
      )}
    </div>
  );
}

export function MessageThread({ messages, isLoading, isTyping, conversationId, participants, customerUserId, onMarkUnread, onToggleStar }: MessageThreadProps) {
  const { user } = useAuth();
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isTyping]);

  if (!conversationId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-neutral-100">
          <svg className="h-7 w-7 text-neutral-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <p className="text-[13px] text-neutral-400">Seleziona una conversazione</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-200 border-t-brand-teal" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-teal/[0.08]">
          <svg className="h-6 w-6 text-brand-teal/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z" />
          </svg>
        </div>
        <div>
          <p className="text-[13px] font-medium text-neutral-500">Inizia la conversazione</p>
          <p className="mt-0.5 text-[11px] text-neutral-400">Scrivi un messaggio per iniziare</p>
        </div>
      </div>
    );
  }

  // Messages come from API sorted newest-first, we reverse for display
  const sorted = [...messages].reverse();
  const lastIndex = sorted.length - 1;

  let lastDateStr = '';
  let lastSenderId = '';

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 lg:px-10">
      {sorted.map((msg, idx) => {
        const isMine = msg.senderId === user?.id;
        const dateStr = formatDateSeparator(msg.createdAt);
        const showDateSep = dateStr !== lastDateStr;
        const isSameSender = msg.senderId === lastSenderId && !showDateSep;

        // Show avatar + name at the start of a new group from the other party
        const isNewOtherGroup = !isMine && !isSameSender && participants;

        lastDateStr = dateStr;
        lastSenderId = msg.senderId;

        // Check if next message is from same sender (for tail rounding)
        const nextMsg = sorted[idx + 1];
        const nextIsSame = nextMsg && nextMsg.senderId === msg.senderId
          && formatDateSeparator(nextMsg.createdAt) === dateStr;

        // Avatar visible only on the first message of a group from the other party
        const showAvatar = isNewOtherGroup;

        // Super-admin oversight: the last message, if sent by the user (i.e. the
        // professional has not replied), shows a "mark unread" icon.
        const canMarkUnread =
          !!onMarkUnread &&
          idx === lastIndex &&
          !!customerUserId &&
          String(msg.senderId) === String(customerUserId);

        return (
          <div key={msg.id}>
            {/* Date separator */}
            {showDateSep && (
              <div className="my-5 flex items-center justify-center">
                <span className="rounded-md bg-neutral-100 px-3.5 py-1.5 text-[11px] font-medium uppercase tracking-wider text-neutral-400">
                  {dateStr}
                </span>
              </div>
            )}

            {/* Message row */}
            <div
              className={`group flex items-end ${isMine ? 'justify-end' : 'justify-start'} ${
                isSameSender ? 'mt-[3px]' : showDateSep ? '' : 'mt-4'
              }`}
            >
              {/* Other party avatar — shown at start of group, invisible spacer for subsequent */}
              {!isMine && (
                <div className="mr-2.5 flex w-8 shrink-0 flex-col justify-end">
                  {showAvatar ? (
                    <div className="relative h-8 w-8">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100">
                        <img src="/images/logo-navy.png" alt="" className="h-5 w-5 object-contain opacity-60" />
                      </div>
                      {participants?.otherAvatar && (
                        <img
                          src={participants.otherAvatar}
                          alt={participants.otherName}
                          className="absolute inset-0 h-8 w-8 rounded-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Stella a sinistra dei miei messaggi (allineati a destra) */}
              {onToggleStar && isMine && (
                <StarToggle
                  starred={!!msg.isStarred}
                  onToggle={() => onToggleStar(msg.id, !msg.isStarred)}
                />
              )}

              <HoverTimestamp timestamp={msg.createdAt} isMine={isMine}>
                <div
                  className={`px-4 py-2.5 ${
                    isMine
                      ? `bg-brand-teal text-white ${
                          !nextIsSame ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-r-md'
                        }`
                      : `bg-neutral-100 text-neutral-800 ${
                          !nextIsSame ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl rounded-l-md'
                        }`
                  }`}
                >
                  {/* Sender name inside first bubble of group */}
                  {isNewOtherGroup && (
                    <p className="mb-0.5 text-[11px] font-semibold text-brand-teal/70">
                      {participants.otherName}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words text-[14.5px] leading-[1.5]">
                    {msg.content}
                  </p>
                  <div className={`mt-1 flex items-center gap-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <span className={`text-[11px] tabular-nums ${isMine ? 'text-white/60' : 'text-neutral-400'}`}>
                      {formatMessageTime(msg.createdAt)}
                    </span>
                    {isMine && <StatusIcon status={msg.status} />}
                  </div>
                </div>
              </HoverTimestamp>

              {onToggleStar && !isMine && (
                <StarToggle
                  starred={!!msg.isStarred}
                  onToggle={() => onToggleStar(msg.id, !msg.isStarred)}
                />
              )}

              {/* Super-admin: mark this (last unanswered) user message as unread */}
              {canMarkUnread && (
                <button
                  type="button"
                  onClick={() => onMarkUnread?.()}
                  aria-label="Segna come da leggere"
                  title="Segna come da leggere (riattiva la notifica per il professionista)"
                  className="ml-1.5 mb-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-brand-orange/10 hover:text-brand-orange"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75l8.4 5.25a2.25 2.25 0 002.7 0l8.4-5.25M3.75 18.75h16.5a1.5 1.5 0 001.5-1.5V6.75a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v10.5a1.5 1.5 0 001.5 1.5z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Typing indicator */}
      {isTyping && (
        <div className="mt-3 flex items-end justify-start">
          {/* Avatar spacer to align with messages */}
          <div className="mr-2.5 w-8 shrink-0" />
          <div className="rounded-2xl rounded-bl-md bg-neutral-100 px-4 py-2.5">
            <div className="flex items-center gap-1">
              <span className="inline-block h-[5px] w-[5px] animate-[bounce_1.2s_ease-in-out_infinite] rounded-full bg-neutral-400" />
              <span className="inline-block h-[5px] w-[5px] animate-[bounce_1.2s_ease-in-out_infinite_0.15s] rounded-full bg-neutral-400" />
              <span className="inline-block h-[5px] w-[5px] animate-[bounce_1.2s_ease-in-out_infinite_0.3s] rounded-full bg-neutral-400" />
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
